import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { type FC, useState, useCallback, useEffect } from "react";

import { Card } from "@/components/ui";
import { debugLog } from "@/domains/logging/utils/debugLog";

/**
 * WHAT:
 *   Portfolio import tab component for importing PDFs to Supabase database with progress tracking.
 *
 * WHY:
 *   Provides user interface for bulk importing portfolio transaction PDFs with real-time progress feedback.
 *
 * PROPS:
 *   None (root tab component)
 *
 * BEHAVIOR:
 *   - Allows directory selection for batch PDF import
 *   - Shows real-time progress during import process
 *   - Displays import statistics (processed, imported, failed, skipped)
 *   - Listens to backend progress events via Tauri event system
 *   - Handles import errors and displays error messages
 *
 * RISKS:
 *   - NOTE: Directory selection may fail on some file systems
 *   - NOTE: Progress events are asynchronous - may not reflect real-time state
 *   - WARN: Large PDF batches may cause memory issues
 *   - NOTE: Error handling may not cover all edge cases
 *
 * @returns {JSX.Element} Import tab component
 *
 */
export const ImportTab: FC = () => {
  // Component mounted - only log once
  useEffect(() => {
    debugLog("info", "investments", "ImportTab mounted", { module: "parqet" });
  }, []);

  // --- STATE ---
  const [isImporting, setIsImporting] = useState(false);
  const [progress, setProgress] = useState({
    current_file: "",
    failed: 0,
    imported: 0,
    processed: 0,
    skipped: 0,
    total: 0,
  });
  const [finished, setFinished] = useState(false);
  const [selectedDirectory, setSelectedDirectory] = useState<null | string>(null);
  interface ImportError {
    broker_detected?: string;
    error_message?: string;
    error_type?: string;
    file_name?: string;
    file_path?: string;
    message?: string;
    parsed_data?: boolean | null | number | Record<string, unknown> | string | unknown[];
    source_hash?: string;
    transaction_type?: string;
  }

  const [errorDetails, setErrorDetails] = useState<ImportError[]>([]);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [detailedErrors] = useState<ImportError[]>([]);
  const [importResult, setImportResult] = useState<null | {
    batch_id: string;
    broker: string;
    total_count: number;
    success_count: number;
    error_count: number;
    duration_ms: number;
  }>(null);

  // --- EVENT LISTENERS ---
  useEffect(() => {
    // Listen for import progress events (updated for new parallel function)
    const unlistenProgress = listen("import_progress", (event) => {
      const payload = event.payload as {
        processed: number;
        total: number;
        currentFile: string;
        successful: number;
        failed: number;
        skipped: number;
        phase: string;
      };

      // Map new structure to old structure for compatibility
      setProgress({
        current_file: payload.currentFile,
        failed: payload.failed,
        imported: payload.successful,
        processed: payload.processed,
        skipped: payload.skipped,
        total: payload.total,
      });

      if (payload.phase === "parsing") {
        setIsImporting(true);
        setFinished(false);
      }
    });

    // Listen for import completion
    const unlistenDone = listen("import_progress_done", (event) => {
      const payload = event.payload as {
        status: string;
        imported: number; // ✅ Korrekte Felder!
        failed: number;
        skipped: number;
        total: number;
        trace_id: string;
      };

      if (payload.status === "completed") {
        debugLog("info", "investments", "Import completed", {
          failed: payload.failed,
          imported: payload.imported,
          module: "parqet",
          skipped: payload.skipped,
        });

        setIsImporting(false);
        setFinished(true);
        setProgress((previous) => ({
          ...previous,
          failed: payload.failed,
          imported: payload.imported, // ✅ Korrekt!
          skipped: payload.skipped,
        }));

        debugLog("info", "investments", "Import completed", {
          failed: payload.failed,
          imported: payload.imported, // ✅ Korrekt!
          module: "parqet",
          skipped: payload.skipped,
        });
      }
    });

    return () => {
      void unlistenProgress.then((f) => {
        f();
      });
      void unlistenDone.then((f) => {
        f();
      });
    };
  }, []);

  /** Select directory for import */
  const handleDirectorySelect = useCallback(async () => {
    try {
      const { open } = await import("@tauri-apps/plugin-dialog");
      const selectedPath = await open({
        directory: true,
        title: "Wähle Ordner mit PDF-Dateien für Import",
      });

      if (!selectedPath || Array.isArray(selectedPath)) {
        return;
      }

      setSelectedDirectory(selectedPath);
      debugLog("info", "investments", "Directory selected", {
        module: "parqet",
        path: selectedPath,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error) || "Unbekannter Fehler";
      debugLog("error", "investments", "Folder selection failed", {
        error: message,
        file: "ImportTab.tsx",
        function: "handleDirectorySelect",
        module: "parqet",
      });
    }
  }, []);

  /**
   * WHAT:
   *   Loads import errors for a specific batch ID.
   *
   * WHY:
   *   Separates error loading logic to reduce cognitive complexity.
   *
   * @param {string} batchId - Batch ID to load errors for
   *
   * @param {number} errorCount - Expected error count
   *
   * @returns {Promise<void>}
   *
   */
  const loadImportErrors = useCallback(
    async (batchId: string, errorCount: number): Promise<void> => {
      debugLog("info", "investments", "Loading errors for batch", {
        batch_id: batchId,
        error_count: errorCount,
        module: "parqet",
      });
      try {
        const loadedErrors = await invoke<ImportError[]>("get_import_errors_by_batch_id", {
          batchId,
        });
        debugLog("debug", "investments", "Received errors from backend", {
          isArray: Array.isArray(loadedErrors),
          length: Array.isArray(loadedErrors) ? loadedErrors.length : 0,
          module: "parqet",
          type: typeof loadedErrors,
        });
        setErrorDetails(Array.isArray(loadedErrors) ? loadedErrors : []);
        if (Array.isArray(loadedErrors) && loadedErrors.length > 0) {
          setShowErrorDetails(true); // Automatisch anzeigen
        } else {
          debugLog("warn", "investments", "No errors found in database for batch", {
            batch_id: batchId,
            module: "parqet",
          });
        }
      } catch (error_) {
        const errorMessage = error_ instanceof Error ? error_.message : String(error_);
        debugLog("error", "investments", "Failed to load errors", {
          error: errorMessage,
          file: "ImportTab.tsx",
          function: "loadImportErrors",
          module: "parqet",
        });
      }
    },
    [],
  );

  /**
   * WHAT:
   *   Starts the PDF import process for selected directory.
   *
   * WHY:
   *   Initiates batch import of PDF files with progress tracking and error handling.
   *
   * BEHAVIOR:
   *   - Validates directory is selected
   *   - Resets progress state
   *   - Calls backend import command
   *   - Handles import results and errors
   *   - Automatically loads error details if import fails
   *
   * SIDE EFFECTS:
   *   - Sets importing state
   *   - Resets progress counters
   *   - Updates import result state
   *   - Loads error details if errors occur
   *
   * RISKS:
   *   - NOTE: Uses alert() for user feedback - not ideal UX
   *   - NOTE: Large directories may cause long-running operations
   *   - NOTE: Error loading may fail silently
   */
  const handleImport = useCallback(async () => {
    // NOTE: Validate directory is selected before starting import
    if (!selectedDirectory) {
      alert("Bitte wähle zuerst einen Ordner aus.");
      return;
    }

    try {
      setIsImporting(true);
      setFinished(false);
      // NOTE: Reset progress state for new import operation
      setProgress({
        current_file: "",
        failed: 0,
        imported: 0,
        processed: 0,
        skipped: 0,
        total: 0,
      });

      debugLog("info", "investments", "Starting import", {
        directory: selectedDirectory,
        module: "parqet",
      });

      // NOTE: Call backend import command - v2 supports parallel processing
      const result = await invoke<{
        batch_id: string;
        broker: string;
        total_count: number;
        success_count: number;
        error_count: number;
        duration_ms: number;
      }>("import_pdf_to_db_v2", {
        directoryPath: selectedDirectory,
      });

      // ✅ NEUE RESULT-STRUKTUR
      if (typeof result === "object") {
        const typedResult = result as {
          batch_id: string;
          broker: string;
          total_count: number;
          success_count: number;
          error_count: number;
          duration_ms: number;
        };

        debugLog("info", "investments", "Import results", {
          batch_id: typedResult.batch_id,
          broker: typedResult.broker,
          duration_ms: typedResult.duration_ms,
          error_count: typedResult.error_count,
          module: "parqet",
          success_count: typedResult.success_count,
          total_count: typedResult.total_count,
        });

        // Update progress mit finalen Werten
        setProgress((previous) => ({
          ...previous,
          failed: typedResult.error_count,
          imported: typedResult.success_count,
          skipped: typedResult.total_count - typedResult.success_count - typedResult.error_count,
          total: typedResult.total_count,
        }));

        // Store import result for batch report
        setImportResult(typedResult);

        // ✅ Automatisch Fehler laden wenn es welche gibt
        if (typedResult.error_count > 0) {
          await loadImportErrors(typedResult.batch_id, typedResult.error_count);
        } else {
          debugLog("info", "investments", "No errors to load (error_count = 0)", {
            batch_id: typedResult.batch_id,
            module: "parqet",
          });
        }

        setFinished(true);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error) || "Unbekannter Fehler";
      setIsImporting(false);
      setFinished(false);
      debugLog("error", "investments", "Import failed", {
        error: message,
        file: "ImportTab.tsx",
        function: "handleImport",
        module: "parqet",
      });
    } finally {
      setIsImporting(false);
    }
  }, [selectedDirectory, loadImportErrors]);

  // --- RENDER ---
  return (
    <div
      className="h-full flex flex-col space-y-6 overflow-y-auto parqet-import-scrollbar"
      style={{
        scrollbarColor: "#475569 #1e293b",
        scrollbarWidth: "thin",
      }}
    >
      <style>{`
        .parqet-import-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .parqet-import-scrollbar::-webkit-scrollbar-track {
          background: #1e293b;
          border-radius: 4px;
        }
        .parqet-import-scrollbar::-webkit-scrollbar-thumb {
          background: #475569;
          border-radius: 4px;
        }
        .parqet-import-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #64748b;
        }
      `}</style>
      {/* Header */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-tracq-bright-blue/20 rounded-lg">
            <svg className="w-6 h-6 text-tracq-bright-blue" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2Z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Portfolio Import</h1>
            <p className="text-slate-400">Import PDFs to Supabase database</p>
          </div>
        </div>
      </Card>

      {/* Import Controls */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="default"
      >
        <h2 className="text-xl font-semibold text-white mb-4">📥 PDF Import</h2>

        <div className="space-y-4">
          {/* Directory Selection */}
          <div className="flex items-center gap-3">
            <button
              className="px-4 py-2 bg-tracq-bright-blue/20 text-tracq-bright-blue rounded-lg text-sm font-medium hover:bg-tracq-bright-blue/30 disabled:opacity-50 transition"
              disabled={isImporting}
              onClick={() => {
                void handleDirectorySelect();
              }}
            >
              📁 Ordner auswählen
            </button>
            {selectedDirectory ? (
              <div className="text-slate-300 text-sm">
                <span className="text-slate-400">Ausgewählt:</span> {selectedDirectory}
              </div>
            ) : null}
          </div>

          {/* Import Button */}
          <div className="flex items-center gap-3">
            <button
              className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium hover:bg-green-600/30 disabled:opacity-50 transition"
              disabled={!selectedDirectory || isImporting}
              onClick={() => {
                void handleImport();
              }}
            >
              {isImporting ? "Importing..." : "🚀 Import starten"}
            </button>
            {finished ? (
              <div className="text-green-400 text-sm">✅ Import abgeschlossen!</div>
            ) : null}
          </div>
        </div>
      </Card>

      {/* Progress Display */}
      {isImporting ? (
        <Card className="bg-blue-500/10 border-blue-500/30" padding="lg" variant="default">
          <div className="flex items-center gap-3">
            <div className="animate-spin w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full" />
            <div>
              <div className="font-medium text-blue-400">Importing PDFs...</div>
              <div className="text-sm text-blue-300">
                {progress.current_file ? `📄 ${progress.current_file}` : null}
              </div>
              <div className="text-xs text-blue-400 mt-1">
                Progress: {progress.processed} / {progress.total || "?"} PDFs
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {/* Results / Batch Report */}
      {finished && importResult ? (
        <Card
          className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
          padding="lg"
          variant="default"
        >
          <h3 className="text-lg font-semibold text-white mb-4">📊 Import Batch Report</h3>

          {/* Batch Info */}
          <div className="mb-4 p-3 bg-slate-600/20 rounded-lg">
            <div className="text-slate-300 text-sm space-y-1">
              <div>
                <span className="text-slate-400">Batch ID:</span>{" "}
                <code className="text-xs bg-slate-800 px-2 py-1 rounded">
                  {importResult.batch_id}
                </code>
              </div>
              <div>
                <span className="text-slate-400">Broker:</span> {importResult.broker}
              </div>
              <div>
                <span className="text-slate-400">Dauer:</span>{" "}
                {(importResult.duration_ms / 1000).toFixed(2)}s
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <div className="text-green-400 font-semibold text-lg">
                ✅ {importResult.success_count}
              </div>
              <div className="text-green-300 text-sm">Erfolgreich importiert</div>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
              <div className="text-red-400 font-semibold text-lg">
                ❌ {importResult.error_count}
              </div>
              <div className="text-red-300 text-sm">Fehlerhafte PDFs</div>
              {importResult.error_count > 0 && (
                <button
                  className="mt-2 px-3 py-1 bg-red-600/20 text-red-400 rounded text-xs hover:bg-red-600/30 transition"
                  onClick={() => {
                    setShowErrorDetails(!showErrorDetails);
                  }}
                >
                  {showErrorDetails ? "🔍 Details verstecken" : "🔍 Details anzeigen"}
                </button>
              )}
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="text-yellow-400 font-semibold text-lg">⏭️ {progress.skipped}</div>
              <div className="text-yellow-300 text-sm">Übersprungen (Duplikate)</div>
            </div>
          </div>
          <div className="mt-4 text-slate-400 text-sm">
            Gesamt verarbeitet: {importResult.total_count} PDFs
          </div>
        </Card>
      ) : null}

      {/* Error Summary - Compact & User-Friendly */}
      {finished && detailedErrors.length > 0 ? (
        <Card className="bg-red-900/20 border-red-500/30" padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2">
            <span>⚠️</span>
            <span>Fehler beim Import</span>
          </h3>

          <div className="space-y-2">
            {detailedErrors.map((error, index) => {
              // Parse debug info from message
              const [mainMessage, debugInfo] = (error.message ?? "").split("\n\nDebug:");

              return (
                <div
                  className="flex flex-col gap-2 p-3 bg-slate-800/40 rounded-lg"
                  key={error.source_hash ?? error.file_name ?? `error-${String(index)}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-xl mt-0.5">
                      {error.error_type === "SUPABASE_TABLE_MISSING" && "🗄️"}
                      {error.error_type === "VALIDATION_ERROR" && "⚠️"}
                      {error.error_type === "PARSE_ERROR" && "📄"}
                      {error.error_type === "DUPLICATE" && "⏭️"}
                      {error.error_type === "SYNC_ERROR" && "🔄"}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-white truncate text-sm">
                        {error.file_name}
                      </div>
                      <div className="text-red-300 text-sm mt-0.5">{mainMessage}</div>
                    </div>
                  </div>

                  {/* Debug Info Section */}
                  {debugInfo ? (
                    <div className="ml-8 p-2 bg-slate-900/60 rounded border border-slate-700">
                      <div className="text-xs text-slate-400 font-mono whitespace-pre-wrap">
                        {debugInfo}
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          {/* Quick Fix Hint for Supabase Error */}
          {detailedErrors.some((e) => e.error_type === "SUPABASE_TABLE_MISSING") && (
            <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <div className="text-sm text-blue-300">
                <strong>Lösung:</strong> SQL-Migration ausführen:{" "}
                <code className="text-xs bg-slate-800 px-2 py-1 rounded">
                  CREATE TABLE import_runs (...)
                </code>
              </div>
            </div>
          )}
        </Card>
      ) : null}

      {/* Debug Info */}
      {process.env.NODE_ENV === "development" && (
        <Card className="bg-yellow-500/10 border-yellow-500/30" padding="lg" variant="default">
          <h3 className="text-lg font-semibold text-yellow-400 mb-2">🐛 Debug Info</h3>
          <div className="text-yellow-300 text-sm space-y-1">
            <div>showErrorDetails: {showErrorDetails.toString()}</div>
            <div>errorDetails.length: {errorDetails.length}</div>
            <div>progress.failed: {progress.failed}</div>
          </div>
        </Card>
      )}

      {/* Error Details - Verbesserte Anzeige */}
      {showErrorDetails && errorDetails.length > 0 ? (
        <Card
          className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
          padding="lg"
          variant="default"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">
              ❌ Fehlerdetails ({errorDetails.length})
            </h3>
            <button
              className="px-3 py-1 bg-slate-600/30 text-slate-300 rounded text-sm hover:bg-slate-600/50 transition"
              onClick={() => {
                setShowErrorDetails(false);
              }}
            >
              ✕ Schließen
            </button>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {errorDetails.map((error, index) => (
              <div
                className="bg-red-900/20 border border-red-500/30 rounded-lg p-4"
                key={error.source_hash ?? error.file_name ?? `error-detail-${String(index)}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="text-red-400 font-medium text-sm mb-1">
                      📄 {error.file_name ?? "Unbekannte Datei"}
                    </div>
                    {error.transaction_type ? (
                      <div className="text-red-300 text-xs mb-1">
                        Typ:{" "}
                        <span className="font-mono bg-red-900/30 px-1 rounded">
                          {error.transaction_type}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div className="text-red-300 text-xs ml-4">
                    {typeof error.broker_detected === "string"
                      ? error.broker_detected
                      : "Unknown Broker"}
                  </div>
                </div>

                <div className="text-red-200 text-sm mb-2">
                  <strong>Fehler:</strong> {error.error_message ?? "Unbekannter Fehler"}
                </div>

                {error.parsed_data !== null && error.parsed_data !== undefined && (
                  <details className="mt-2">
                    <summary className="text-red-300 text-xs cursor-pointer hover:text-red-200 select-none">
                      🔍 Parsed Data anzeigen
                    </summary>
                    <pre className="mt-2 p-2 bg-slate-900/60 rounded text-xs text-slate-300 overflow-x-auto max-h-48 overflow-y-auto">
                      {(() => {
                        const parsed = error.parsed_data;
                        try {
                          if (typeof parsed === "object" && !Array.isArray(parsed)) {
                            return JSON.stringify(parsed, null, 2);
                          }
                          if (Array.isArray(parsed)) {
                            return JSON.stringify(parsed, null, 2);
                          }
                          if (
                            typeof parsed === "string" ||
                            typeof parsed === "number" ||
                            typeof parsed === "boolean"
                          ) {
                            return String(parsed);
                          }
                          return JSON.stringify(parsed, null, 2);
                        } catch {
                          return "Error displaying parsed data";
                        }
                      })()}
                    </pre>
                  </details>
                )}

                {error.source_hash ? (
                  <div className="text-red-300 text-xs mt-1 font-mono">
                    Hash:{" "}
                    <span className="bg-red-900/30 px-1 rounded">
                      {error.source_hash.slice(0, 16)}...
                    </span>
                  </div>
                ) : null}

                {error.file_path ? (
                  <div className="text-red-300 text-xs mt-1 opacity-75">
                    Path: {error.file_path}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {/* Future Features */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="default"
      >
        <h3 className="text-lg font-semibold text-white mb-4">🚀 Coming Soon</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-slate-600/20 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">CSV Import</h4>
            <p className="text-sm text-slate-400">Import portfolio data from CSV files</p>
          </div>
          <div className="bg-slate-600/20 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">API Integration</h4>
            <p className="text-sm text-slate-400">Connect to broker APIs for automatic sync</p>
          </div>
          <div className="bg-slate-600/20 rounded-lg p-4">
            <h4 className="font-medium text-white mb-2">Bulk Processing</h4>
            <p className="text-sm text-slate-400">Process multiple files at once</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
